import { BaseUnit } from './BaseUnit';
import { Enemy } from '../Enemy';
import { Projectile } from '../Projectile';
import { ARCHER, WALL_X, WALL_WIDTH, GAME_WIDTH } from '../../config';
import { StickFigureGraphics } from '../../rendering/StickFigureGraphics';

export class Archer extends BaseUnit {
  protected attackTimer: number = 0;
  protected drawPhase: number = 0;
  protected projectiles: Projectile[];

  protected config: any = ARCHER;

  constructor(scene: Phaser.Scene, x: number, y: number, slotIndex: number, projectiles: Projectile[]) {
    super(scene, x, y, slotIndex);
    this.projectiles = projectiles;

    // Initialize stats from config
    this.accuracyPenalty = this.config.accuracyPenalty || 0;
    this.targetPriority = 'backline';

    this.draw();
  }

  getDamage(): number {
    const perUnitDamage = Math.round(this.config.baseDamage * this.bonusDamageMult * this.globalDamageMult);
    return perUnitDamage * this.stackCount;
  }

  get pierceCount(): number {
    return (this.config.basePierceCount || 0) + this.bonusPierceCount;
  }

  update(time: number, delta: number, enemies: Enemy[]): void {
    if (this.drawPhase > 0) {
      this.drawPhase -= delta * 0.003;
      if (this.drawPhase < 0) this.drawPhase = 0;
    }


    this.attackTimer -= delta;
    if (this.attackTimer <= 0) {
      // Range = Base + Bonus
      const range = this.config.range + (this.rangeBonus || 0);
      const target = this.findTarget(enemies, range);
      if (target) {
        this.attackTimer = this.getEffectiveAttackInterval(this.config.attackInterval);
        this.drawPhase = 1;
        this.fire(target);
        if (this.doubleShot) {
          this.fireWithOffset(target, 0, 15);
        }
      } else {
        this.attackTimer = 0;
      }
    }

    this.draw();
  }

  protected calculateMissChance(target: Enemy): number {
    const penalty = this.accuracyPenalty;

    if (penalty <= 0) return 0;

    const wallEdge = WALL_X + WALL_WIDTH;
    const maxFieldWidth = GAME_WIDTH - wallEdge;
    const distance = target.x - wallEdge;
    const distRatio = Math.max(0, Math.min(1, distance / maxFieldWidth));
    return distRatio * this.accuracyPenalty;
  }

  protected fire(target: Enemy): void {
    this.fireWithOffset(target, 0, 0);
  }

  protected fireWithOffset(target: Enemy, offsetX: number, offsetY: number): void {
    const damage = this.getDamage();
    let targetX = target.x + offsetX;
    let targetY = target.y + offsetY;

    const missChance = this.calculateMissChance(target);
    if (missChance > 0 && Math.random() < missChance) {
      const missOffset = 40 + Math.random() * 60;
      targetX += (Math.random() - 0.5) * missOffset * 2;
      targetY += (Math.random() > 0.5 ? 1 : -1) * missOffset;

      // Show "MISS" floating text via scene's floating text manager
      const gameScene = this.scene as any;
      if (gameScene.floatingTextManager) {
        gameScene.floatingTextManager.spawn(this.x, this.y - 30, 'MISS', 'error');
      }

      const proj = new Projectile(this.scene, this.x + 10, this.y - 10, {
        damage: 0,
        speed: this.config.projectileSpeed,
        piercing: false,
        projectileType: 'arrow',
        targetX,
        targetY,
        poisonDamage: this.poisonEnabled ? 1 : 0,
        poisonDuration: this.poisonEnabled ? 1 : 0,
        fireDamage: this.fireEnabled ? 1 : 0,
        fireDuration: this.fireEnabled ? 1 : 0,
      });
      this.projectiles.push(proj);
      return;
    }

    const proj = new Projectile(this.scene, this.x + 10, this.y - 10, {
      damage,
      speed: this.config.projectileSpeed,
      piercing: this.pierceCount > 0,
      maxPierceCount: this.pierceCount,
      projectileType: 'arrow',
      targetX,
      targetY,
      poisonDamage: this.poisonEnabled ? Math.round(damage * ((this.config.poisonPercent || 0) + this.poisonPercent)) : 0,
      poisonDuration: this.poisonEnabled ? this.poisonDuration : 0,
      fireDamage: this.fireEnabled ? Math.round(damage * this.firePercent) : 0,
      fireDuration: this.fireEnabled ? this.fireDuration : 0,
    });
    this.projectiles.push(proj);
  }

  draw(): void {
    if (this.drawPhase <= 0) {
      this.sprite.setTexture('friendly_archer_idle');
    } else {
      let frame = Math.floor(this.drawPhase * 8);
      if (frame < 0) frame = 0;
      if (frame > 7) frame = 7;
      this.sprite.setTexture(`friendly_archer_${frame}`);
    }

    this.graphics.clear();

    // Level indicator (green for poison)
    if (this.level > 1) {
      this.graphics.fillStyle(0x44cc44, 1);
      for (let i = 0; i < Math.min(this.level - 1, 5); i++) {
        this.graphics.fillCircle(this.x - 8 + i * 4, this.y + 16, 1.5);
      }
    }
  }
}
